/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, Clock, X } from 'lucide-react';
import { FADE_UP_BANNER } from '@/lib/motionPresets';
import { metaForStep } from './nextStepMeta';
import { useOnboardingCta } from './useOnboardingCta';

const STORAGE_KEY = 'sc_onb_banner_dismissed';

const ReviewBanner = ({ onDismiss }) => (
  <div className="p-4 flex items-start gap-3">
    <div className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-muted text-muted-foreground">
      <Clock className="h-5 w-5" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-display font-semibold text-sm mb-0.5" data-testid="onboarding-banner-title">Verifica Albo in corso</div>
      <p className="text-xs text-muted-foreground leading-relaxed">
        Il nostro team sta verificando la tua iscrizione all'Albo. Riceverai una email al completamento (di solito entro 24–48h).
      </p>
    </div>
    <button type="button" onClick={onDismiss} aria-label="Nascondi" data-testid="onboarding-banner-close" className="p-1 rounded-full hover:bg-secondary transition-colors flex-shrink-0">
      <X className="h-4 w-4 text-muted-foreground" />
    </button>
  </div>
);

const ProgressBanner = ({ meta, onContinue, onDismiss }) => (
  <>
    <div className="h-1.5 bg-primary/10 relative" aria-hidden="true">
      <div className="h-full bg-primary-gradient rounded-r-full transition-all duration-500" style={{ width: `${meta.percent}%` }} data-testid="onboarding-banner-progress" />
    </div>
    <div className="p-4 flex items-start gap-3">
      <div className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 relative bg-primary/15 text-primary">
        <CheckCircle2 className="h-5 w-5" />
        <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
          {meta.percent}%
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold text-sm mb-0.5" data-testid="onboarding-banner-title">
          Sei al {meta.percent}% — {meta.label.toLowerCase()}
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          {meta.detailLong || 'Ancora pochi passi e il tuo profilo sarà verificato e prenotabile dai pazienti.'}
        </p>
        {meta.incentive && (
          <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/30 border border-amber-300/50 text-amber-800 dark:text-amber-200 px-2.5 py-0.5 text-[11px] font-semibold" data-testid="onboarding-banner-incentive">
            {meta.incentive}
          </div>
        )}
        <div className="mt-2.5 flex items-center gap-2">
          <button type="button" onClick={onContinue} data-testid="onboarding-banner-cta" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 transition-opacity">
            Continua ora <ArrowRight className="h-3 w-3" />
          </button>
          <button type="button" onClick={onDismiss} data-testid="onboarding-banner-dismiss" className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1">
            Più tardi
          </button>
        </div>
      </div>
    </div>
  </>
);

/**
 * Full-width banner variant, useful at the top of dashboard/feed pages.
 * Consumes the shared onboarding context — no independent HTTP call.
 */
export const OnboardingBanner = () => {
  const navigate = useNavigate();
  const { visible, status, dismiss } = useOnboardingCta(STORAGE_KEY);
  if (!visible) return null;

  const meta = metaForStep(status.next_step);
  const isUnderReview = status.next_step === 'tier2_awaiting_review';

  return (
    <motion.div
      {...FADE_UP_BANNER}
      role="region"
      aria-label="Onboarding status"
      data-testid="onboarding-banner"
      className={`mb-3 rounded-2xl border overflow-hidden ${isUnderReview ? 'bg-secondary/50 border-border' : 'bg-primary/5 border-primary/25'}`}
    >
      {isUnderReview
        ? <ReviewBanner onDismiss={dismiss} />
        : <ProgressBanner meta={meta} onContinue={() => navigate('/onboarding')} onDismiss={dismiss} />}
    </motion.div>
  );
};
